import { type Dispatch, type SetStateAction, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import BulkManageDesktop from "common/components/BulkManageDesktop";
import { PaginatedDataTable } from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import RestrictedComponent from "common/components/RestrictedComponent";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";
import type { Campaign } from "common/types";

import {
  CAMPAIGN_OUTREACH_OPTIONS,
  CAMPAIGNS_COLUMNS,
  MANAGE_CAMPAIGNS_ACTIONS,
} from "../../../constants";
import { CampaignActionsDropdown } from "../../CampaignActions";
import CampaignsActions from "../CampaignsActions";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { JSX } from "react";

type CampaignColumnId = keyof typeof CAMPAIGNS_COLUMNS;

const LOADING_SKELETONS: Record<CampaignColumnId, JSX.Element> = {
  owners: <Skeleton className="h-6 w-32 rounded-full" />,
  state: <Skeleton className="h-6 w-16" />,
  type: <Skeleton className="h-6 w-16" />,
  created_at: <Skeleton className="h-6 w-20" />,
  success: <Skeleton className="h-[18px] w-16" />,
  completion: (
    <div className="flex items-center gap-x-1">
      <Skeleton className="w-8 h-4" />
      <Skeleton className="h-2 w-24" />
    </div>
  ),
};

/** Columns whose id is a `Campaign` field, so they can be sorted by `?sort=`. */
const ACCESSOR_KEYS: Partial<Record<CampaignColumnId, keyof Campaign>> = {
  state: "state",
  created_at: "created_at",
};

interface DesktopTableProps {
  isLoading: boolean;
  campaigns: Campaign[];
  selectedCampaigns: Campaign[];
  setSelectedCampaigns: Dispatch<SetStateAction<Campaign[]>>;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
}

export default function DesktopTable({
  isLoading,
  campaigns,
  selectedCampaigns,
  setSelectedCampaigns,
  columnVisibility,
  setColumnVisibility,
}: DesktopTableProps) {
  const navigate = useNavigate();
  const { permissions } = useSelectedWorkspaceContext();

  function onRowClick(campaign: Campaign) {
    navigate(campaign.id);
  }

  const columns = useMemo(() => {
    const campaignColumns: ColumnDef<Campaign>[] = [
      {
        id: "name",
        accessorKey: "name",
        header: "Campaigns",
        cell: ({
          row: {
            original: { outreach_type, name },
          },
        }) => {
          const { icon: SubtypeIcon } =
            CAMPAIGN_OUTREACH_OPTIONS[outreach_type] ?? {};

          return (
            <div className="flex flex-col gap-y-1">
              {isLoading ? (
                <Skeleton className="h-5 w-40" />
              ) : (
                <h5
                  className="line-clamp-1 break-all text-md-bold"
                  title={name}
                >
                  {name}
                </h5>
              )}

              <div className="flex items-center gap-1">
                {isLoading ? (
                  <>
                    <Skeleton className="size-4" />
                    <Skeleton className="h-4 w-40" />
                  </>
                ) : (
                  <>
                    <SubtypeIcon className="size-4 text-black-600" />
                    <span className="line-clamp-1 break-all text-button-xs text-black-500">
                      {outreach_type === "drip" ? "1st" : "2nd & 3rd+"}{" "}
                      connections
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(CAMPAIGNS_COLUMNS).map(
        ([id, { label, icon, renderTag }]): ColumnDef<Campaign> => ({
          id,
          accessorKey: ACCESSOR_KEYS[id],
          header: label,
          cell: ({ row: { original } }) => {
            if (isLoading) {
              return LOADING_SKELETONS[id];
            }
            return renderTag(original, true);
          },
          meta: { icon, className: "min-w-40 max-w-40" },
        }),
      ),
      {
        id: "actions",
        cell: ({ row: { original } }) => {
          if (isLoading)
            return <Skeleton className="inline-flex size-8 rounded-full" />;

          return (
            <RestrictedComponent visibleFor={["campaigns_full"]}>
              <CampaignActionsDropdown campaign={original} />
            </RestrictedComponent>
          );
        },
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ];

    return campaignColumns;
  }, [isLoading]);

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "campaigns",
      selectedRows: selectedCampaigns,
      onRowSelectionChange: permissions.includes("campaigns_full")
        ? setSelectedCampaigns
        : undefined,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
    reactTableOptions: { data: campaigns, columns },
    isLoading,
  });

  return (
    <PaginatedDataTable
      table={table}
      onRowClick={onRowClick}
      isLoading={isLoading}
      selectionActionsComponent={
        <BulkManageDesktop
          actions={MANAGE_CAMPAIGNS_ACTIONS}
          selection={selectedCampaigns}
          clearSelection={() => setSelectedCampaigns([])}
          renderActionComponent={({ action, onClose }) => (
            <CampaignsActions
              action={action}
              selectedCampaigns={selectedCampaigns}
              onBack={onClose}
              onSuccess={() => {
                onClose();
                setSelectedCampaigns([]);
              }}
            />
          )}
        />
      }
    />
  );
}
